/**
 * ClientCallUs - Contact/Call component
 * Contact info, click-to-call, live chat placeholder, callback scheduling
 */

import React, { useState, useEffect } from 'react';
import { clientFetch, getClientData } from './api';
import ClientShell from './ClientShell';

export default function ClientCallUs() {
  const clientData = getClientData();
  const currentTier = clientData?.tier || 'smb';
  const [profileData, setProfileData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const [callbackForm, setCallbackForm] = useState({
    date: '',
    time: '',
    method: 'phone',
    description: '',
  });
  const [callbackSubmitting, setCallbackSubmitting] = useState(false);
  const [callbackSuccess, setCallbackSuccess] = useState(false);

  useEffect(() => {
    // Fetch account manager info
    clientFetch('/client/portal/profile')
      .then((r) => r.json())
      .then((data) => {
        setProfileData(data);
      })
      .catch(() => {});

    // Initialize chat with welcome message
    setChatMessages([
      {
        type: 'agent',
        text: 'Hi! Welcome to Talk Australia support. How can we help you today?',
        timestamp: new Date(),
      },
    ]);
  }, []);

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;

    const userMessage = {
      type: 'user',
      text: chatInput,
      timestamp: new Date(),
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput('');
    setChatSubmitting(true);

    try {
      // Post chat to tickets API
      const res = await clientFetch('/client/portal/tickets', {
        method: 'POST',
        body: JSON.stringify({
          type: 'chat',
          message: chatInput,
          category: 'support',
        }),
      });

      if (res.ok) {
        // Simulate agent response
        setTimeout(() => {
          setChatMessages((prev) => [
            ...prev,
            {
              type: 'agent',
              text: 'Thank you for your message. Our support team will respond shortly.',
              timestamp: new Date(),
            },
          ]);
        }, 500);
      }
    } catch (err) {
      console.error('Error sending chat message', err);
    }
    setChatSubmitting(false);
  };

  const handleCallbackSubmit = async () => {
    if (!callbackForm.date || !callbackForm.time) {
      alert('Please fill in date and time');
      return;
    }
    setCallbackSubmitting(true);
    try {
      const res = await clientFetch('/client/portal/tickets', {
        method: 'POST',
        body: JSON.stringify({
          type: 'callback',
          scheduledDate: callbackForm.date,
          scheduledTime: callbackForm.time,
          method: callbackForm.method,
          description: callbackForm.description,
          category: 'callback_request',
        }),
      });

      if (res.ok) {
        setCallbackSuccess(true);
        setCallbackForm({ date: '', time: '', method: 'phone', description: '' });
        setTimeout(() => setCallbackSuccess(false), 3000);
      }
    } catch (err) {
      alert('Error submitting callback request');
    }
    setCallbackSubmitting(false);
  };

  const styles = {
    container: {
      maxWidth: '1200px',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '24px',
      marginBottom: '32px',
    },
    gridFull: {
      gridColumn: '1 / -1',
    },
    card: {
      backgroundColor: '#111827',
      border: '1px solid #2A3444',
      borderRadius: '8px',
      padding: '24px',
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #2A3444',
    },
    contactItem: {
      marginBottom: '20px',
      paddingBottom: '16px',
      borderBottom: '1px solid #2A3444',
    },
    contactLabel: {
      fontSize: '12px',
      color: '#8B92A5',
      fontWeight: '600',
      marginBottom: '4px',
      textTransform: 'uppercase',
    },
    contactValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#FFFFFF',
      marginBottom: '8px',
    },
    contactSubtext: {
      fontSize: '13px',
      color: '#8B92A5',
    },
    ctaBtn: {
      width: '100%',
      padding: '12px 16px',
      backgroundColor: '#00D4AA',
      border: 'none',
      borderRadius: '6px',
      color: '#0A0F1D',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
      marginTop: '12px',
    },
    emergencyBadge: {
      display: 'inline-block',
      padding: '8px 12px',
      backgroundColor: 'rgba(255, 107, 107, 0.2)',
      border: '1px solid #FF6B6B',
      borderRadius: '6px',
      color: '#FF6B6B',
      fontSize: '12px',
      fontWeight: '600',
      marginBottom: '12px',
    },
    chatContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '500px',
    },
    messagesArea: {
      flex: 1,
      overflowY: 'auto',
      marginBottom: '12px',
      paddingRight: '8px',
    },
    message: {
      marginBottom: '12px',
      display: 'flex',
      animation: 'fadeIn 0.3s ease-in',
    },
    messageUser: {
      justifyContent: 'flex-end',
    },
    messageBubble: {
      maxWidth: '75%',
      padding: '10px 12px',
      borderRadius: '6px',
      fontSize: '13px',
      wordWrap: 'break-word',
    },
    messageBubbleAgent: {
      backgroundColor: '#1A2332',
      color: '#BBBEC7',
      borderLeft: '3px solid #0057FF',
    },
    messageBubbleUser: {
      backgroundColor: '#0057FF',
      color: '#FFFFFF',
    },
    messageTime: {
      fontSize: '11px',
      color: '#5A6175',
      marginTop: '4px',
      textAlign: 'right',
    },
    chatInput: {
      display: 'flex',
      gap: '8px',
    },
    chatInputField: {
      flex: 1,
      padding: '10px 12px',
      backgroundColor: '#0A0F1D',
      border: '1px solid #2A3444',
      borderRadius: '6px',
      color: '#FFFFFF',
      fontSize: '13px',
      fontFamily: 'inherit',
    },
    chatSendBtn: {
      padding: '10px 16px',
      backgroundColor: '#0057FF',
      border: 'none',
      borderRadius: '6px',
      color: '#FFFFFF',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '13px',
      transition: 'all 0.2s',
    },
    formGroup: {
      marginBottom: '16px',
    },
    formLabel: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '600',
      color: '#BBBEC7',
      marginBottom: '6px',
    },
    formInput: {
      width: '100%',
      padding: '10px 12px',
      backgroundColor: '#0A0F1D',
      border: '1px solid #2A3444',
      borderRadius: '6px',
      color: '#FFFFFF',
      fontSize: '14px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    formTextarea: {
      width: '100%',
      padding: '10px 12px',
      backgroundColor: '#0A0F1D',
      border: '1px solid #2A3444',
      borderRadius: '6px',
      color: '#FFFFFF',
      fontSize: '13px',
      fontFamily: 'inherit',
      minHeight: '80px',
      resize: 'vertical',
      boxSizing: 'border-box',
    },
    submitBtn: {
      width: '100%',
      padding: '10px 16px',
      backgroundColor: '#0057FF',
      border: 'none',
      borderRadius: '6px',
      color: '#FFFFFF',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '13px',
      transition: 'all 0.2s',
      marginTop: '12px',
    },
    successMessage: {
      backgroundColor: 'rgba(0, 212, 170, 0.2)',
      border: '1px solid #00D4AA',
      color: '#00D4AA',
      padding: '12px 16px',
      borderRadius: '6px',
      fontSize: '13px',
      fontWeight: '600',
      marginBottom: '16px',
      textAlign: 'center',
    },
    managerCard: {
      backgroundColor: '#1A2332',
      border: '1px solid #0057FF',
      borderRadius: '8px',
      padding: '16px',
      marginTop: '12px',
    },
    managerName: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#FFFFFF',
      marginBottom: '4px',
    },
    managerTitle: {
      fontSize: '12px',
      color: '#8B92A5',
      marginBottom: '8px',
    },
    managerContact: {
      fontSize: '12px',
      color: '#BBBEC7',
      marginBottom: '4px',
    },
  };

  return (
    <ClientShell title="Contact & Support">
      <div style={styles.container}>
        <div style={styles.grid}>
          {/* Contact Information */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>Contact Talk Australia</div>

            <div style={styles.contactItem}>
              <div style={styles.contactLabel}>Phone</div>
              <div style={styles.contactValue}>1300 TALK AU</div>
              <div style={styles.contactSubtext}>(1300 825 528)</div>
              <a href="tel:1300825528" style={styles.ctaBtn}>
                ☎️ Click to Call
              </a>
            </div>

            <div style={styles.contactItem}>
              <div style={styles.contactLabel}>Email Support</div>
              <div style={styles.contactValue}>support@talkaustralia.com.au</div>
              <div style={styles.contactSubtext}>Response time: 2-4 hours</div>
              <a
                href="mailto:support@talkaustralia.com.au"
                style={styles.ctaBtn}
              >
                ✉️ Send Email
              </a>
            </div>

            <div style={styles.contactItem}>
              <div style={styles.contactLabel}>Address</div>
              <div style={styles.contactValue}>IT &amp; T Communications Pty Ltd t/a Talk Australia</div>
              <div style={styles.contactSubtext}>
                Level 3, 45 Barangaroo Avenue
                <br />
                Sydney NSW 2000, Australia
              </div>
            </div>

            {currentTier === 'enterprise' && (
              <>
                <div style={styles.emergencyBadge}>🚨 24/7 Priority Support</div>
                <div style={styles.contactItem}>
                  <div style={styles.contactLabel}>Emergency IT Support</div>
                  <div style={styles.contactValue}>1300 EMERGENCY</div>
                  <div style={styles.contactSubtext}>(Available 24/7 for Enterprise)</div>
                  <a href="tel:1300370000" style={styles.ctaBtn}>
                    ☎️ Call Now
                  </a>
                </div>
              </>
            )}
          </div>

          {/* Account Manager */}
          {profileData && (
            <div style={styles.card}>
              <div style={styles.cardTitle}>Your Account Manager</div>
              <div style={styles.managerCard}>
                <div style={styles.managerName}>
                  {profileData.account_manager_name || 'Sarah Johnson'}
                </div>
                <div style={styles.managerTitle}>
                  Senior Account Manager
                </div>
                <div style={styles.managerContact}>
                  📧 {profileData.account_manager_email || 'sarah.johnson@talkaustralia.com.au'}
                </div>
                <div style={styles.managerContact}>
                  ☎️ {profileData.account_manager_phone || '+61 2 8000 8000 ext 4521'}
                </div>
                <div style={styles.contactSubtext} style={{ marginTop: '12px' }}>
                  {currentTier === 'enterprise'
                    ? 'Dedicated account manager available 24/7'
                    : 'Your dedicated point of contact for support and account matters'}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Live Chat */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Live Chat Support</div>
          <div style={styles.chatContainer}>
            <div style={styles.messagesArea}>
              {chatMessages.map((msg, idx) => (
                <div
                  key={idx}
                  style={{
                    ...styles.message,
                    ...(msg.type === 'user' ? styles.messageUser : {}),
                  }}
                >
                  <div>
                    <div
                      style={{
                        ...styles.messageBubble,
                        ...(msg.type === 'user'
                          ? styles.messageBubbleUser
                          : styles.messageBubbleAgent),
                      }}
                    >
                      {msg.text}
                    </div>
                    <div style={styles.messageTime}>
                      {msg.timestamp.toLocaleTimeString('en-AU', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div style={styles.chatInput}>
              <input
                type="text"
                placeholder="Type your message..."
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleChatSend();
                }}
                style={styles.chatInputField}
              />
              <button
                style={styles.chatSendBtn}
                onClick={handleChatSend}
                disabled={chatSubmitting}
              >
                {chatSubmitting ? '...' : '→'}
              </button>
            </div>
          </div>
        </div>

        {/* Schedule Callback */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Schedule a Callback</div>

          {callbackSuccess && (
            <div style={styles.successMessage}>
              ✓ Callback request scheduled successfully!
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Preferred Date *</label>
              <input
                type="date"
                style={styles.formInput}
                value={callbackForm.date}
                onChange={(e) => setCallbackForm({ ...callbackForm, date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Preferred Time *</label>
              <input
                type="time"
                style={styles.formInput}
                value={callbackForm.time}
                onChange={(e) => setCallbackForm({ ...callbackForm, time: e.target.value })}
              />
            </div>

            <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
              <label style={styles.formLabel}>Contact Method</label>
              <select
                style={styles.formInput}
                value={callbackForm.method}
                onChange={(e) => setCallbackForm({ ...callbackForm, method: e.target.value })}
              >
                <option value="phone">Phone Call</option>
                <option value="email">Email</option>
                <option value="video">Video Call</option>
              </select>
            </div>

            <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
              <label style={styles.formLabel}>What would you like to discuss?</label>
              <textarea
                style={styles.formTextarea}
                placeholder="E.g. Upgrade plan, billing question, technical support..."
                value={callbackForm.description}
                onChange={(e) => setCallbackForm({ ...callbackForm, description: e.target.value })}
              />
            </div>
          </div>

          <button
            style={styles.submitBtn}
            onClick={handleCallbackSubmit}
            disabled={callbackSubmitting}
          >
            {callbackSubmitting ? 'Scheduling...' : 'Schedule Callback'}
          </button>
        </div>

        {/* Additional Resources */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Additional Resources</div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
            }}
          >
            <button
              style={{
                ...styles.ctaBtn,
                backgroundColor: 'transparent',
                border: '1px solid #0057FF',
                color: '#0057FF',
                marginTop: 0,
              }}
            >
              📚 Knowledge Base
            </button>
            <button
              style={{
                ...styles.ctaBtn,
                backgroundColor: 'transparent',
                border: '1px solid #0057FF',
                color: '#0057FF',
                marginTop: 0,
              }}
            >
              🎓 Training Videos
            </button>
            <button
              style={{
                ...styles.ctaBtn,
                backgroundColor: 'transparent',
                border: '1px solid #0057FF',
                color: '#0057FF',
                marginTop: 0,
              }}
            >
              📋 Documentation
            </button>
            <button
              style={{
                ...styles.ctaBtn,
                backgroundColor: 'transparent',
                border: '1px solid #0057FF',
                color: '#0057FF',
                marginTop: 0,
              }}
            >
              ⚙️ System Status
            </button>
          </div>
        </div>
      </div>
    </ClientShell>
  );
}
